/** Length distribution, drift over time, and prayer request coverage. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { BarsChart, TrendChart, DonutChart } from '../components/charts'
import {
  Card, Tile, QueryState, Empty, fmtInt, downloadCsv, PartnerLabel, AlumniSwitch,
} from '../components/ui'
import { useRelabel } from '../lib/terms'

export default function TextAnalytics() {
  const t = useRelabel()
  const [withAlumni, setWithAlumni] = useState(false)
  const query = useApi(
    ['stats-text', withAlumni],
    `stats/text${withAlumni ? '?include_alumni=1' : ''}`
  )

  return (
    <PageHeader
      title="Text Analytics"
      sub="How long updates are and how often they carry prayer requests"
      actions={<AlumniSwitch checked={withAlumni} onChange={setWithAlumni} where="updates" />}
    >
      <QueryState query={query}>
        {(d) => {
          const pct = d.prayer.total ? Math.round((d.prayer.with_prayer / d.prayer.total) * 100) : 0
          const latest = d.byYear[d.byYear.length - 1]
          const earliest = d.byYear[0]

          return (
            <div className="col">
              <div className="grid grid-4">
                <Tile label="Updates" value={fmtInt(d.prayer.total)} />
                <Tile
                  label="With prayer requests"
                  value={`${pct}%`}
                  note={`${fmtInt(d.prayer.with_prayer)} of ${fmtInt(d.prayer.total)}`}
                />
                {/* Two counts left. The average prayer request length had a tile —
                    a mean over every update including the ones with no request at
                    all, which is a number it is hard to do anything with — and the
                    longest update had another, which the table at the foot lists
                    along with the next nine, each one a link. */}
              </div>

              <div className="grid grid-2">
                <Card title="Length Distribution" sub="Updates bucketed by word count">
                  <BarsChart data={d.buckets} x="bucket" series={[{ key: 'n', label: 'Updates' }]} height={250} />
                </Card>
                <Card title="Prayer Request Coverage">
                  <DonutChart
                    data={[
                      { name: 'With prayer requests', value: d.prayer.with_prayer },
                      { name: 'Without', value: d.prayer.total - d.prayer.with_prayer },
                    ]}
                    height={250}
                  />
                </Card>
              </div>

              <Card
                title="Length over Time"
                sub={
                  earliest && latest
                    ? `Average went from ${fmtInt(earliest.avg_words)} words in ${earliest.year} to ${fmtInt(latest.avg_words)} in ${latest.year}`
                    : undefined
                }
                actions={
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      downloadCsv('text-by-year.csv', d.byYear, [
                        { key: 'year', label: 'Year' },
                        { key: 'avg_words', label: 'Avg words' },
                        { key: 'max_words', label: 'Longest' },
                      ])
                    }
                  >
                    ↓ CSV
                  </button>
                }
              >
                <TrendChart
                  data={d.byYear}
                  x="year"
                  series={[
                    { key: 'avg_words', label: 'Average words' },
                    { key: 'max_words', label: 'Longest that year' },
                  ]}
                  height={280}
                />
              </Card>

              <Card title="Longest Updates">
                {d.longest.length ? (
                  <div className="table-wrap flush">
                    <table className="data compact">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>{t('Partner Unit')}</th>
                          <th className="right">Words</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {d.longest.map((r) => (
                          <tr key={r.id}>
                            <td className="nowrap">
                              <Link className="mono" to={`/newsletters/${r.id}`}>
                                {r.published_on}
                              </Link>
                            </td>
                            <td>
                              <PartnerLabel code={r.s4ca_code} name={r.partner_name} link={false} />
                            </td>
                            <td className="num">{fmtInt(r.word_count)}</td>
                            <td>
                              <Link className="btn btn-sm" to={`/newsletters/${r.id}`}>
                                Read
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty title="No updates" />
                )}
              </Card>
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
